/**
 * Reusable Stepper UI Component
 * A multi-step indicator component for forms and wizards.
 */

import { CheckIcon } from "@/assets/svg";

export interface StepperStep {
  readonly label: string;
}

export interface StepperProps {
  readonly steps: StepperStep[];
  readonly currentStep: number;
  readonly className?: string;
}

// Color constants
const COLORS = {
  completed: "#007C4C", // Green
  active: "#FEAE00", // Orange
  inactive: "#DFE2E7", // Gray
};

// Get connector line style based on adjacent step statuses
const getConnectorStyle = (
  leftCompleted: boolean,
  leftActive: boolean,
  rightCompleted: boolean,
  rightActive: boolean
): React.CSSProperties => {
  const leftColor = leftCompleted
    ? COLORS.completed
    : leftActive
      ? COLORS.active
      : COLORS.inactive;
  const rightColor = rightCompleted
    ? COLORS.completed
    : rightActive
      ? COLORS.active
      : COLORS.inactive;

  // If both sides have the same color, use solid color
  if (leftColor === rightColor) {
    return { backgroundColor: leftColor };
  }

  // Otherwise, use a gradient (split color effect)
  return {
    background: `linear-gradient(to right, ${leftColor} 50%, ${rightColor} 50%)`,
  };
};

export const Stepper = ({
  steps,
  currentStep,
  className = "",
}: StepperProps): React.JSX.Element => {
  return (
    <div className={`flex items-center justify-center w-full ${className}`}>
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = index === steps.length - 1;

        // Calculate next step status for connector line
        const nextStepNumber = index + 2;
        const isNextActive = nextStepNumber === currentStep;
        const isNextCompleted = nextStepNumber < currentStep;

        return (
          <div key={index} className="flex items-center">
            {/* Step Circle and Label */}
            <div className="flex flex-col items-center">
              {/* Outer ring wrapper - only visible for active step */}
              <div
                className={`
                  rounded-full flex items-center justify-center
                  transition-all duration-200
                  ${isActive ? "p-1 border-2 border-[#FEAE00]" : "p-0 border-2 border-transparent"}
                `}
              >
                {/* Step circle */}
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                    transition-all duration-200
                    ${
                      isCompleted
                        ? "bg-[#007C4C] text-white border-2 border-[#007C4C]"
                        : isActive
                          ? "bg-[#FEAE00] text-white border-2 border-[#FEAE00]"
                          : "bg-[#DFE2E7] text-[#001A41] border-2 border-[#DFE2E7]"
                    }
                  `}
                >
                  {isCompleted ? <CheckIcon /> : stepNumber}
                </div>
              </div>
              <span
                className={`
                  mt-2 text-base font-semibold whitespace-nowrap
                  ${isActive || isCompleted ? "text-[#001A41]" : "text-[#9CA3AF]"}
                `}
              >
                {step.label}
              </span>
            </div>

            {/* Connector Line */}
            {!isLast && (
              <div
                className="w-40 h-0.5 mx-4 mt-[-1.5rem] transition-all duration-200"
                style={getConnectorStyle(
                  isCompleted,
                  isActive,
                  isNextCompleted,
                  isNextActive
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

